import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FONT = "'Open Sans', sans-serif";
const ACCENT = 'var(--od-c-1982c4, #1982C4)';
const TEXT_PRIMARY = 'var(--od-c-181d27, #181D27)';
const TEXT_SECONDARY = 'var(--od-c-414651, #414651)';
const TEXT_MUTED = 'var(--od-c-717680, #717680)';
const TEXT_DISABLED = 'var(--od-c-a4a7ae, #A4A7AE)';
const BORDER = 'var(--od-c-d5d7da, #D5D7DA)';
const BORDER_LIGHT = 'var(--od-c-e9eaeb, #E9EAEB)';

export const withPinnedLast = (fields) => {
  if (!fields.some((f) => f.pinnedLast)) return fields;
  return [...fields.filter((f) => !f.pinnedLast), ...fields.filter((f) => f.pinnedLast)];
};

const FieldRow = ({ field, isVisible, isLocked, onToggle }) => {
  const dragDisabled = isLocked || !!field.pinnedLast;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id, disabled: dragDisabled });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 4px',
    borderRadius: 6,
    background: isDragging ? 'var(--od-c-f5f5f5, #F5F5F5)' : 'transparent',
  };

  const checkboxStyle = {
    width: 18,
    height: 18,
    borderRadius: 4,
    border: isVisible ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
    background: isVisible ? ACCENT : 'var(--od-c-ffffff, #fff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: isLocked ? 'not-allowed' : 'pointer',
    transition: 'background 0.1s ease, border-color 0.1s ease',
  };

  const labelStyle = {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: 15,
    lineHeight: '22px',
    color: isVisible ? TEXT_PRIMARY : TEXT_DISABLED,
    margin: 0,
    flex: '1 0 0',
    cursor: isLocked ? 'default' : 'pointer',
    userSelect: 'none',
  };

  return (
    <div ref={setNodeRef} style={rowStyle} {...attributes}>
      <div
        {...(dragDisabled ? {} : listeners)}
        style={{
          width: 16,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: dragDisabled ? 'not-allowed' : 'grab',
          flexShrink: 0,
          color: dragDisabled ? BORDER : TEXT_DISABLED,
        }}
        aria-label="Drag to reorder"
      >
        <i className="fas fa-grip-vertical" style={{ fontSize: 14 }} />
      </div>
      <div
        role="checkbox"
        aria-checked={isVisible}
        aria-disabled={isLocked}
        tabIndex={isLocked ? -1 : 0}
        onClick={() => { if (!isLocked) onToggle(field.id); }}
        onKeyDown={(e) => {
          if (isLocked) return;
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onToggle(field.id);
          }
        }}
        style={checkboxStyle}
      >
        {isVisible && (
          <i className="fas fa-check" style={{ fontSize: 11, color: 'var(--od-c-ffffff, #fff)' }} />
        )}
      </div>
      <p
        style={labelStyle}
        onClick={() => { if (!isLocked) onToggle(field.id); }}
      >
        {field.label}
        {isLocked && (
          <i
            className="fal fa-lock"
            style={{ marginLeft: 8, fontSize: 11, color: TEXT_DISABLED }}
            aria-label="Always visible"
          />
        )}
      </p>
    </div>
  );
};

FieldRow.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    pinnedLast: PropTypes.bool,
  }).isRequired,
  isVisible: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const TableSettings = ({
  visibleFields,
  hiddenFields,
  onFieldsChange,
  onResetToDefault,
  sortBy,
  onSortByChange,
  sortOptions,
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const visibleIds = useMemo(() => new Set(visibleFields.map((f) => f.id)), [visibleFields]);

  const orderedFields = useMemo(
    () => withPinnedLast([...visibleFields, ...hiddenFields]),
    [visibleFields, hiddenFields]
  );

  const filteredFields = useMemo(() => {
    if (!searchTerm) return orderedFields;
    const needle = searchTerm.toLowerCase();
    return orderedFields.filter((f) => f.label.toLowerCase().includes(needle));
  }, [orderedFields, searchTerm]);

  const toggleableCount = useMemo(
    () => orderedFields.filter((f) => !f.alwaysVisible).length,
    [orderedFields]
  );

  const visibleToggleableCount = useMemo(
    () => visibleFields.filter((f) => !f.alwaysVisible).length,
    [visibleFields]
  );

  const allShown = visibleToggleableCount === toggleableCount && toggleableCount > 0;

  const handleFieldToggle = (fieldId) => {
    const field = orderedFields.find((f) => f.id === fieldId);
    if (!field || field.alwaysVisible) return;

    const isCurrentlyVisible = visibleIds.has(fieldId);

    if (isCurrentlyVisible) {
      onFieldsChange(
        visibleFields.filter((f) => f.id !== fieldId),
        [...hiddenFields, field]
      );
    } else {
      onFieldsChange(
        withPinnedLast([...visibleFields, field]),
        hiddenFields.filter((f) => f.id !== fieldId)
      );
    }
  };

  const handleToggleAll = () => {
    if (allShown) {
      const stillVisible = visibleFields.filter((f) => f.alwaysVisible);
      const newlyHidden = visibleFields.filter((f) => !f.alwaysVisible);
      onFieldsChange(withPinnedLast(stillVisible), [...hiddenFields, ...newlyHidden]);
    } else {
      onFieldsChange(withPinnedLast([...visibleFields, ...hiddenFields]), []);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIsVisible = visibleIds.has(active.id);
    const overIsVisible = visibleIds.has(over.id);

    if (activeIsVisible && overIsVisible) {
      const oldIndex = visibleFields.findIndex((f) => f.id === active.id);
      const newIndex = visibleFields.findIndex((f) => f.id === over.id);
      onFieldsChange(withPinnedLast(arrayMove(visibleFields, oldIndex, newIndex)), hiddenFields);
    } else if (!activeIsVisible && !overIsVisible) {
      const oldIndex = hiddenFields.findIndex((f) => f.id === active.id);
      const newIndex = hiddenFields.findIndex((f) => f.id === over.id);
      onFieldsChange(visibleFields, arrayMove(hiddenFields, oldIndex, newIndex));
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 8,
        width: 360,
        maxHeight: '80vh',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--od-c-ffffff, #ffffff)',
        borderRadius: 12,
        boxShadow: '0 12px 24px -8px rgba(16, 24, 40, 0.15), 0 4px 8px -4px rgba(16, 24, 40, 0.08)',
        overflow: 'hidden',
        border: `1px solid ${BORDER_LIGHT}`,
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px 12px',
      }}>
        <p style={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 18,
          lineHeight: '24px',
          color: TEXT_PRIMARY,
          margin: 0,
        }}>
          Columns
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TEXT_MUTED,
          }}
        >
          <i className="fal fa-times" style={{ fontSize: 18 }} />
        </button>
      </div>

      <div style={{ padding: '0 20px 12px' }}>
        {sortBy && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}>
            <p style={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: 14,
              color: TEXT_SECONDARY,
              margin: 0,
            }}>
              Sort by
            </p>
            {sortOptions && onSortByChange ? (
              <select
                aria-label="Sort by"
                value={sortBy}
                onChange={(e) => onSortByChange(e.target.value)}
                style={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 13,
                  color: TEXT_SECONDARY,
                  background: 'var(--od-c-ffffff, #fff)',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: 13, color: TEXT_MUTED }}>{sortBy}</span>
            )}
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          background: 'var(--od-c-ffffff, #fff)',
        }}>
          <i className="fal fa-search" style={{ color: TEXT_MUTED, fontSize: 14 }} />
          <input
            type="text"
            placeholder="Search columns"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: '1 0 0',
              border: 'none',
              outline: 'none',
              fontSize: 15,
              lineHeight: '22px',
              fontFamily: FONT,
              color: TEXT_SECONDARY,
              backgroundColor: 'transparent',
            }}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                color: TEXT_MUTED,
                fontSize: 12,
              }}
            >
              <i className="fal fa-times-circle" />
            </button>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 20px 8px',
      }}>
        <p style={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 12,
          lineHeight: '18px',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: TEXT_MUTED,
          margin: 0,
        }}>
          {visibleToggleableCount} of {toggleableCount} shown
        </p>
        {toggleableCount > 0 && (
          <button
            type="button"
            onClick={handleToggleAll}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: 14,
              color: ACCENT,
            }}
          >
            {allShown ? 'Hide all' : 'Show all'}
          </button>
        )}
      </div>

      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '0 16px 12px',
      }}>
        {filteredFields.length === 0 ? (
          <div style={{ padding: '32px 12px', textAlign: 'center' }}>
            <p style={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: 14,
              color: TEXT_SECONDARY,
              margin: '0 0 4px',
            }}>
              No columns found
            </p>
            <p style={{
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: 13,
              color: TEXT_MUTED,
              margin: 0,
            }}>
              Try a different search term.
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              {filteredFields.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  isVisible={visibleIds.has(field.id)}
                  isLocked={!!field.alwaysVisible}
                  onToggle={handleFieldToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      {onResetToDefault && (
        <div style={{
          borderTop: `1px solid ${BORDER_LIGHT}`,
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={onResetToDefault}
            style={{
              border: `1px solid ${BORDER}`,
              background: 'var(--od-c-ffffff, #fff)',
              cursor: 'pointer',
              padding: '8px 16px',
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: 14,
              color: TEXT_PRIMARY,
              borderRadius: 8,
            }}
          >
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
};

TableSettings.propTypes = {
  visibleFields: PropTypes.array.isRequired,
  hiddenFields: PropTypes.array.isRequired,
  onFieldsChange: PropTypes.func.isRequired,
  onResetToDefault: PropTypes.func,
  sortBy: PropTypes.string,
  onSortByChange: PropTypes.func,
  sortOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TableSettings;
